<template>
    <div class="chat-window" :class="{ 'mobile-view': isMobileView, 'compact-mode': compactMode }">
        <!-- Conversation list (left panel) -->
        <div
            class="conversations-panel"
            :class="{ 'hidden-mobile': activeConversation && (isMobileView || compactMode) }"
        >
            <div class="panel-header">
                <h2>Messages</h2>
                <ChatUnreadBadge
                    v-if="totalUnread > 0"
                    :count="totalUnread"
                />
            </div>

            <ChatConversationList
                :conversations="conversations"
                :current-user-id="currentUserId"
                :active-conversation-id="activeConversation && activeConversation._id"
                :online-users="onlineUsers"
                :typing-users="typingUsers"
                :loading="loadingConversations"
                :show-new-button="isAccountManager"
                @select="handleSelectConversation"
                @new-conversation="$emit('new-conversation')"
            />
        </div>

        <!-- Chat panel (right panel) -->
        <div
            class="chat-panel"
            :class="{ 'hidden-mobile': !activeConversation && (isMobileView || compactMode) }"
        >
            <template v-if="activeConversation">
                <ChatHeader
                    :participant="otherParticipant"
                    :is-online="isParticipantOnline"
                    :is-typing="isParticipantTyping"
                    :last-seen="otherParticipant && otherParticipant.lastSeen"
                    :show-back-button="isMobileView || compactMode"
                    :show-close-button="showCloseButton"
                    @back="handleBack"
                    @close="$emit('close')"
                    @show-info="$emit('show-info', activeConversation)"
                    @menu-action="handleMenuAction"
                />

                <ChatMessageList
                    :messages="currentMessages"
                    :current-user-id="currentUserId"
                    :loading="loadingMessages"
                    :has-more="hasMoreMessages"
                    :typing-users="currentTypingUsers"
                    :participant-name="otherParticipantName"
                    @load-more="$emit('load-more-messages')"
                />

                <ChatInput
                    :disabled="inputDisabled"
                    :placeholder="inputPlaceholder"
                    :max-file-size="maxFileSize"
                    :allowed-file-types="allowedFileTypes"
                    @send="handleSendMessage"
                    @typing-start="handleTypingStart"
                    @typing-stop="handleTypingStop"
                    @file-select="handleFileSelect"
                />
            </template>

            <!-- No conversation selected -->
            <div v-else class="no-conversation">
                <div class="no-conversation-content">
                    <i class="material-icons">forum</i>
                    <h3>Bienvenue dans vos messages</h3>
                    <p>Sélectionnez une conversation pour commencer</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import ChatConversationList from './ChatConversationList.vue';
import ChatHeader from './ChatHeader.vue';
import ChatMessageList from './ChatMessageList.vue';
import ChatInput from './ChatInput.vue';
import ChatUnreadBadge from './ChatUnreadBadge.vue';

export default {
    name: 'ChatWindow',
    components: {
        ChatConversationList,
        ChatHeader,
        ChatMessageList,
        ChatInput,
        ChatUnreadBadge
    },
    props: {
        conversations: {
            type: Array,
            default: () => []
        },
        activeConversation: {
            type: Object,
            default: null
        },
        messages: {
            type: Object,
            default: () => ({})
        },
        currentUserId: {
            type: String,
            required: true
        },
        userType: {
            type: String,
            default: 'Seller'
        },
        onlineUsers: {
            type: [Array, Set, Object],
            default: function() { return {}; }
        },
        typingUsers: {
            type: Object,
            default: () => ({})
        },
        totalUnread: {
            type: Number,
            default: 0
        },
        loadingConversations: {
            type: Boolean,
            default: false
        },
        loadingMessages: {
            type: Boolean,
            default: false
        },
        hasMoreMessages: {
            type: Boolean,
            default: false
        },
        showCloseButton: {
            type: Boolean,
            default: false
        },
        compactMode: {
            type: Boolean,
            default: false
        },
        maxFileSize: {
            type: Number,
            default: 10485760
        },
        allowedFileTypes: {
            type: Array,
            default: () => ['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx', 'xls', 'xlsx']
        }
    },
    data() {
        return {
            isMobileView: false
        };
    },
    computed: {
        isAccountManager() {
            return this.userType === 'AccountManager';
        },
        otherParticipant() {
            if (!this.activeConversation) return null;

            const { seller, accountManager } = this.activeConversation;

            if (seller && seller._id === this.currentUserId) {
                return accountManager;
            }
            return seller;
        },
        otherParticipantName() {
            if (!this.otherParticipant) return '';
            return this.otherParticipant.fullName ||
                   `${this.otherParticipant.firstName || ''} ${this.otherParticipant.lastName || ''}`.trim() ||
                   'Utilisateur';
        },
        otherParticipantId() {
            return (this.otherParticipant && this.otherParticipant._id) || null;
        },
        isParticipantOnline() {
            if (!this.otherParticipantId || !this.onlineUsers) return false;
            if (this.onlineUsers instanceof Set) {
                return this.onlineUsers.has(this.otherParticipantId);
            }
            if (Array.isArray(this.onlineUsers)) {
                return this.onlineUsers.includes(this.otherParticipantId);
            }
            // onlineUsers is an object { oduserId: boolean }
            return this.onlineUsers[this.otherParticipantId] === true;
        },
        isParticipantTyping() {
            if (!this.activeConversation || !this.typingUsers) return false;
            const typingData = this.typingUsers[this.activeConversation._id];
            if (!typingData) return false;
            // typingData is an object { oduserId: boolean }
            if (Array.isArray(typingData)) {
                return typingData.includes(this.otherParticipantId);
            }
            return typingData[this.otherParticipantId] === true;
        },
        currentMessages() {
            if (!this.activeConversation) return [];
            return this.messages[this.activeConversation._id] || [];
        },
        currentTypingUsers() {
            if (!this.activeConversation) return [];
            var typingData = this.typingUsers[this.activeConversation._id];
            if (!typingData) return [];
            // Handle both Array and Object formats
            if (Array.isArray(typingData)) {
                return typingData.filter(function(id) { return id !== this.currentUserId; }.bind(this));
            }
            // typingData is an Object { userId: boolean }
            var self = this;
            var keys = Object.keys(typingData);
            return keys.filter(function(userId) {
                return typingData[userId] === true && userId !== self.currentUserId;
            });
        },
        inputDisabled() {
            return this.activeConversation && this.activeConversation.status === 'blocked';
        },
        inputPlaceholder() {
            if (this.inputDisabled) {
                return 'Cette conversation est bloquée';
            }
            return 'Écrivez votre message...';
        }
    },
    mounted() {
        this.checkMobileView();
        window.addEventListener('resize', this.checkMobileView);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.checkMobileView);
    },
    methods: {
        checkMobileView() {
            this.isMobileView = window.innerWidth < 768;
        },
        handleSelectConversation(conversation) {
            this.$emit('select-conversation', conversation);
        },
        handleBack() {
            this.$emit('clear-active');
        },
        handleSendMessage(data) {
            if (!this.activeConversation) return;

            this.$emit('send-message', {
                conversationId: this.activeConversation._id,
                ...data
            });
        },
        handleTypingStart() {
            if (!this.activeConversation) return;
            this.$emit('typing-start', this.activeConversation._id);
        },
        handleTypingStop() {
            if (!this.activeConversation) return;
            this.$emit('typing-stop', this.activeConversation._id);
        },
        handleFileSelect(file) {
            this.$emit('file-select', {
                conversationId: this.activeConversation && this.activeConversation._id,
                file
            });
        },
        handleMenuAction(action) {
            this.$emit('menu-action', {
                action,
                conversation: this.activeConversation
            });
        }
    }
};
</script>

<style scoped>
.chat-window {
    display: flex;
    height: 100%;
    background-color: #ffffff;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
}

.conversations-panel {
    width: 320px;
    min-width: 280px;
    max-width: 360px;
    border-right: 1px solid #e5e7eb;
    display: flex;
    flex-direction: column;
}

.panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 16px;
    border-bottom: 1px solid #e5e7eb;
}

.panel-header h2 {
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    color: #111827;
}

.chat-panel {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.no-conversation {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f9fafb;
}

.no-conversation-content {
    text-align: center;
    color: #9ca3af;
    padding: 40px;
}

.no-conversation-content i {
    font-size: 64px;
    margin-bottom: 16px;
    opacity: 0.5;
}

.no-conversation-content h3 {
    margin: 0 0 8px;
    font-size: 18px;
    font-weight: 600;
    color: #6b7280;
}

.no-conversation-content p {
    margin: 0;
    font-size: 14px;
}

/* Mobile view */
.mobile-view .conversations-panel,
.compact-mode .conversations-panel {
    width: 100%;
    max-width: none;
    border-right: none;
}

.mobile-view .chat-panel,
.compact-mode .chat-panel {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ffffff;
    z-index: 10;
}

.mobile-view .hidden-mobile,
.compact-mode .hidden-mobile {
    display: none;
}

/* Responsive */
@media (max-width: 768px) {
    .chat-window {
        border-radius: 0;
    }

    .conversations-panel {
        width: 100%;
        max-width: none;
        border-right: none;
    }
}
</style>
